<template>
  <!-- The tooltip is on the container: a disabled button does not receive mouse events (pointer-events: none). -->
  <span
    v-if="canImpersonate"
    class="impersonate-button"
    v-tooltip.top="uniqueEmails.length === 0 ? 'Nessuna email disponibile per questo utente' : ''"
    @click.stop
    @keydown.stop
  >
    <prime-button
      :label="label"
      icon="pi pi-sign-in"
      iconPos="right"
      class="p-button p-button-secondary p-button-sm"
      :disabled="uniqueEmails.length === 0 || busy"
      @click="onClick"
    />
    <prime-menu ref="emailMenu" :model="emailItems" :popup="true" />
  </span>
</template>

<script>
import PrimeButton from 'primevue/button'
import PrimeMenu from 'primevue/menu'
import ImpersonationService from '@/services/ask-management/impersonation-service'

export default {
  name: 'ImpersonateButton',
  components: { PrimeButton, PrimeMenu },

  props: {
    targetType: { type: String, required: true },
    targetId: { type: [Number, String], required: true },
    emails: { type: Array, default: () => [] },
    name: { type: String, default: '' },
  },

  data() {
    return {
      busy: false,
      service: null,
    }
  },

  created() {
    this.service = new ImpersonationService(this.$http, this.$eventBus)
  },

  computed: {
    label() {
      return this.name ? `Impersonifica ${this.name}` : 'Impersonifica'
    },

    // Only profiles with all powers (all = 1). The real check is server-side.
    canImpersonate() {
      try {
        const raw = localStorage.getItem('user')
        const profile = JSON.parse(atob(raw)).user[0].profile
        return Number(profile.all) === 1
      } catch (e) {
        return false
      }
    },

    // The same email written with different capitalization: a single entry (ask_api compares emails in lowercase).
    uniqueEmails() {
      const seen = []
      const unique = []
      this.emails.forEach((email) => {
        const value = typeof email === 'string' ? email.trim() : ''
        const key = value.toLowerCase()
        if (value !== '' && seen.indexOf(key) === -1) {
          seen.push(key)
          unique.push(value)
        }
      })
      return unique
    },

    emailItems() {
      return this.uniqueEmails.map((email) => ({
        label: email,
        icon: 'pi pi-user',
        command: () => this.start(email),
      }))
    },
  },

  methods: {
    onClick(event) {
      if (this.uniqueEmails.length === 1) {
        this.start(this.uniqueEmails[0])
        return
      }
      this.$refs.emailMenu.toggle(event)
    },

    async start(email) {
      if (this.busy) return

      // The tab is opened right away: if we waited for the response, the browser would block it.
      const tab = window.open('', '_blank')
      // Some blockers (e.g. extensions) return an already closed window instead of null.
      if (!tab || tab.closed) {
        // Popup blocked: nothing is requested from ask_api, so no never-used session is left behind.
        this.$toast.add({
          severity: 'warn',
          summary: 'Consenti le finestre popup',
          detail: 'Il browser ha bloccato la nuova scheda. Consenti i popup per questo sito e riprova.',
          life: 8000,
        })
        return
      }

      this.busy = true
      let res
      try {
        res = await this.service.start(this.targetType, this.targetId, email)
      } catch (e) {
        res = { success: false, url: null, message: null }
      } finally {
        this.busy = false
      }

      if (!res.success || !res.url) {
        tab.close()
        this.$toast.add({
          severity: 'error',
          summary: 'Errore',
          detail: res.message || 'Impossibile avviare la sessione. Riprova.',
          life: 5000,
        })
        return
      }

      if (tab.closed) {
        // Tab closed before the response: the one-time link expires by itself after 60 seconds.
        this.$toast.add({
          severity: 'info',
          summary: 'Scheda chiusa',
          detail: 'La nuova scheda è stata chiusa prima dell\'avvio. Premi di nuovo il pulsante per riprovare.',
          life: 5000,
        })
        return
      }

      // The new tab must not be able to control the ASK Admin one.
      tab.opener = null
      tab.location = res.url

      this.$toast.add({
        severity: 'success',
        summary: 'Sessione avviata',
        detail: `Stai accedendo come ${email}. La sessione dura 60 minuti.`,
        life: 5000,
      })
    },
  },
}
</script>

<style scoped>
/* Inline-block: the container covers the whole button, so the tooltip also shows when it is disabled. */
.impersonate-button {
  display: inline-block;
}
</style>
